// v14i — Instellingen: Merken & materialen + E-mail-standaardinstellingen.
// Twee onafhankelijke sub-secties, opgeslagen op user via /api/auth/me/*.
// Geen provider-credentials in deze fase; enkel defaults + neutrale
// "Koppel e-mailprovider"-knop (nog niet actief).
import React, { useEffect, useState } from "react";
import { Palette, Mail, Save, Info, Sparkles, ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import EmailWizard from "./EmailWizard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MATERIAL_CATEGORIES = [
  { key: "binnenverf", label: "Binnenverf" },
  { key: "buitenverf", label: "Buitenverf" },
  { key: "lakwerk", label: "Lakwerk" },
  { key: "buitenschrijnwerk", label: "Buitenschrijnwerk" },
  { key: "decoratieve_afwerkingen", label: "Decoratieve afwerkingen" },
  { key: "behang", label: "Behang" },
];

const MODES = [
  { value: "never", label: "Nooit vermelden" },
  { value: "smart", label: "Alleen indien relevant" },
  { value: "always", label: "Standaard vermelden" },
];

async function _patch(path, body) {
  const r = await fetch(`${API}${path}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json();
}

async function _getMe() {
  const r = await fetch(`${API}/auth/me`, { credentials: "include" });
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json();
}

/* ────────────────────────────────────────────────────────────────
   Merken & materialen sectie
   ──────────────────────────────────────────────────────────────── */
export function MaterialPreferencesSection({ embedded = false } = {}) {
  const [prefs, setPrefs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const me = await _getMe();
        setPrefs(me.material_preferences || {});
      } finally { setLoading(false); }
    })();
  }, []);

  const update = (key, patch) => {
    setPrefs((p) => ({
      ...p,
      [key]: { brand: "", mode: "never", ...(p[key] || {}), ...patch },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const me = await _patch("/auth/me/material-preferences",
        { material_preferences: prefs });
      setPrefs(me.material_preferences || {});
      toast.success("Merkvoorkeuren opgeslagen");
    } catch (e) {
      toast.error("Opslaan mislukt");
    } finally { setSaving(false); }
  };

  if (loading) return null;

  // v14m.1 — embedded=true: sectie wordt binnen CollapsibleSettingsCard
  // gerenderd → geen eigen emma-card wrapper of dubbele header nodig.
  const Wrapper = embedded ? "div" : "div";
  const wrapperClass = embedded
    ? ""
    : "emma-card border border-[#E5E2DC]";
  return (
    <Wrapper className={wrapperClass} data-testid="material-prefs-card">
      {!embedded && (
        <div className="flex items-center gap-2 mb-3">
          <Palette className="w-5 h-5 text-[#B07A4F]" />
          <h2 className="font-heading text-lg font-semibold">Merken & materialen</h2>
        </div>
      )}
      <p className="text-xs text-[#6E6E6C] mb-4">
        Vul je vaste merken in per categorie. Emma verwijst ernaar in offerteteksten
        volgens je keuze — en verzint nooit een merk dat je niet zelf hebt opgegeven.
      </p>
      <div className="space-y-3">
        {MATERIAL_CATEGORIES.map((cat) => {
          const cfg = prefs[cat.key] || { brand: "", mode: "never" };
          return (
            <div
              key={cat.key}
              className="border border-[#F0EFEA] rounded-xl p-3 space-y-2"
              data-testid={`pref-cat-${cat.key}`}
            >
              <div className="flex items-center justify-between gap-2">
                <Label className="text-sm font-semibold">{cat.label}</Label>
              </div>
              <Input
                value={cfg.brand || ""}
                onChange={(e) => update(cat.key, { brand: e.target.value })}
                placeholder="Merk of product (bv. Trimetal, Rubbol, Farrow & Ball)"
                className="text-sm"
                data-testid={`pref-brand-${cat.key}`}
              />
              <div className="flex flex-wrap gap-2">
                {MODES.map((m) => {
                  const active = (cfg.mode || "never") === m.value;
                  return (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => update(cat.key, { mode: m.value })}
                      className={`text-xs rounded-full px-3 py-1.5 border transition ${
                        active
                          ? "bg-[#B07A4F] text-white border-[#B07A4F]"
                          : "bg-white text-[#5D7259] border-[#E5E2DC] hover:bg-[#FAF9F6]"
                      }`}
                      data-testid={`pref-mode-${cat.key}-${m.value}`}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#B07A4F] hover:bg-[#8f6440] text-white h-10 rounded-xl"
          data-testid="material-prefs-save-btn"
        >
          <Save className="w-4 h-4" />
          {saving ? "Bezig…" : "Merkvoorkeuren opslaan"}
        </Button>
      </div>
    </Wrapper>
  );
}

/* ────────────────────────────────────────────────────────────────
   E-mail defaults + SMTP-koppeling sectie
   ──────────────────────────────────────────────────────────────── */
export function EmailSettingsSection({ embedded = false } = {}) {
  const [settings, setSettings] = useState({
    sender_name: "", reply_to: "", default_subject: "",
    default_body: "", signature: "",
  });
  const [smtp, setSmtp] = useState({
    host: "", port: 587, user: "", password: "",
    use_tls: true, from_name: "", from_email: "",
    connected: false, password_masked: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const reload = async () => {
    const me = await _getMe();
    setSettings((s) => ({ ...s, ...(me.email_settings || {}) }));
    setSmtp((s) => ({ ...s, ...(me.smtp_config || {}), password: "" }));
  };

  useEffect(() => {
    (async () => {
      try {
        const me = await _getMe();
        setSettings((s) => ({ ...s, ...(me.email_settings || {}) }));
        setSmtp((s) => ({ ...s, ...(me.smtp_config || {}), password: "" }));
      } finally { setLoading(false); }
    })();
  }, []);

  const update = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  const updateSmtp = (k, v) => setSmtp((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const me = await _patch("/auth/me/email-settings",
        { email_settings: settings });
      setSettings((s) => ({ ...s, ...(me.email_settings || {}) }));
      toast.success("E-mailinstellingen opgeslagen");
    } catch (e) {
      toast.error("Opslaan mislukt");
    } finally { setSaving(false); }
  };

  const saveSmtp = async () => {
    setSmtpSaving(true); setSmtpTestResult(null);
    try {
      const body = {
        smtp_config: {
          host: smtp.host, port: +smtp.port || 587,
          user: smtp.user, use_tls: smtp.use_tls,
          from_name: smtp.from_name, from_email: smtp.from_email || smtp.user,
        },
      };
      if (smtp.password) body.smtp_config.password = smtp.password;
      const me = await _patch("/auth/me/smtp-config", body);
      const c = me.smtp_config || {};
      setSmtp((s) => ({ ...s, ...c, password: "" }));
      if (c.status === "verified") toast.success("Opgeslagen en getest — verbinding gelukt");
      else toast.error(`Opgeslagen, maar test mislukt: ${c.last_test_message || "onbekende fout"}`);
    } catch (e) {
      toast.error("Opslaan SMTP mislukt");
    } finally { setSmtpSaving(false); }
  };

  const testSmtp = async () => {
    setSmtpTesting(true); setSmtpTestResult(null);
    try {
      const r = await fetch(`${API}/auth/me/smtp-test`, {
        method: "POST", credentials: "include",
      });
      const d = await r.json();
      setSmtpTestResult(d);
      if (d.ok) toast.success("Verbinding gelukt");
      else toast.error(d.message || "Verbinding mislukt");
      await reload();
    } catch (e) {
      setSmtpTestResult({ ok: false, message: "Netwerkfout" });
    } finally { setSmtpTesting(false); }
  };

  const sendTestMail = async () => {
    setSmtpTesting(true); setSmtpTestResult(null);
    try {
      const r = await fetch(`${API}/auth/me/smtp-test-email`, { method: "POST", credentials: "include" });
      const d = await r.json();
      const msg = d.ok ? `Testmail naar ${d.to} aanvaard door je mailserver. Kijk in je inbox (en spamfolder).` : (d.message || d.detail || "Testmail mislukt");
      setSmtpTestResult({ ok: !!d.ok, message: msg });
      d.ok ? toast.success("Testmail aanvaard door je mailserver") : toast.error(msg);
      await reload();
    } catch (e) {
      setSmtpTestResult({ ok: false, message: "Netwerkfout" });
    } finally { setSmtpTesting(false); }
  };

  const disconnectSmtp = async () => {
    if (!window.confirm("Verbinding met je mailbox verbreken? De inloggegevens worden verwijderd.")) return;
    try {
      const r = await fetch(`${API}/auth/me/smtp-config`, {
        method: "DELETE", credentials: "include",
      });
      const me = await r.json();
      setSmtp((s) => ({
        ...s, ...(me.smtp_config || {}),
        host: "", port: 587, user: "", password: "",
        use_tls: true, from_name: "", from_email: "",
        connected: false, password_masked: "",
      }));
      setSmtpTestResult(null);
      toast.success("Verbinding verbroken");
    } catch (e) { toast.error("Verbreken mislukt"); }
  };

  if (loading) return null;

  const emailWrapperClass = embedded
    ? ""
    : "emma-card border border-[#E5E2DC]";
  return (
    <div className={emailWrapperClass} data-testid="email-settings-card">
      {!embedded && (
        <div className="flex items-center gap-2 mb-3">
          <Mail className="w-5 h-5 text-[#B07A4F]" />
          <h2 className="font-heading text-lg font-semibold">E-mail</h2>
        </div>
      )}
      <p className="text-xs text-[#6E6E6C] mb-4">
        Standaardinstellingen voor als je een offerte via e-mail wil verzenden.
        Gebruik <code className="text-[10px] bg-[#F0EFEA] px-1 rounded">{"{project_name}"}</code>,
        <code className="text-[10px] bg-[#F0EFEA] px-1 rounded ml-1">{"{quote_number}"}</code> of
        <code className="text-[10px] bg-[#F0EFEA] px-1 rounded ml-1">{"{client_name}"}</code> als plaatsvervangers.
      </p>
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label className="text-sm">Afzendernaam</Label>
          <Input value={settings.sender_name || ""}
            onChange={(e) => update("sender_name", e.target.value)}
            placeholder="Bv. Yves Delvaux — YD Creaties"
            data-testid="email-sender-name" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">Reply-to e-mailadres</Label>
          <Input type="email" value={settings.reply_to || ""}
            onChange={(e) => update("reply_to", e.target.value)}
            placeholder="Bv. yves@ydcreaties.be"
            data-testid="email-reply-to" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">Standaard onderwerp</Label>
          <Input value={settings.default_subject || ""}
            onChange={(e) => update("default_subject", e.target.value)}
            placeholder="Bv. Offerte {quote_number} — {project_name}"
            data-testid="email-subject-template" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">Standaard begeleidend bericht</Label>
          <Textarea rows={4} value={settings.default_body || ""}
            onChange={(e) => update("default_body", e.target.value)}
            placeholder="Bv. Beste {client_name},&#10;&#10;Hierbij onze offerte voor {project_name}."
            data-testid="email-body-template" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">Handtekening</Label>
          <Textarea rows={3} value={settings.signature || ""}
            onChange={(e) => update("signature", e.target.value)}
            placeholder="Bv. Met vriendelijke groet,&#10;YD Creaties · +32 478 541109"
            data-testid="email-signature" />
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <Button onClick={save} disabled={saving}
          className="bg-[#B07A4F] hover:bg-[#8f6440] text-white h-10 rounded-xl"
          data-testid="email-settings-save-btn">
          <Save className="w-4 h-4" />
          {saving ? "Bezig…" : "E-mailinstellingen opslaan"}
        </Button>
      </div>

      {/* v14k — SMTP-koppeling. v14l — Emma-wizard als primaire CTA. */}
      <div className="mt-6 pt-6 border-t border-[#E5E2DC] space-y-3" data-testid="smtp-section">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-heading text-base font-semibold">Rechtstreeks verzenden via je eigen mailbox</h3>
          {smtp.connected && smtp.status === "verified" && (
            <span className="text-[10px] uppercase tracking-wider bg-[#5D7259] text-white rounded-full px-2 py-0.5 font-bold"
              data-testid="smtp-status-connected">Verbonden</span>
          )}
          {smtp.connected && smtp.status === "failed" && (
            <span className="text-[10px] uppercase tracking-wider bg-red-100 text-red-800 rounded-full px-2 py-0.5 font-bold"
              data-testid="smtp-status-failed">Test mislukt</span>
          )}
          {smtp.connected && smtp.status !== "verified" && smtp.status !== "failed" && (
            <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 rounded-full px-2 py-0.5 font-bold"
              data-testid="smtp-status-untested">Opgeslagen, niet getest</span>
          )}
          {!smtp.connected && (
            <span className="text-[10px] uppercase tracking-wider bg-[#F0EFEA] text-[#6E6E6C] rounded-full px-2 py-0.5"
              data-testid="smtp-status-disconnected">Niet verbonden</span>
          )}
        </div>
        {smtp.connected && smtp.last_test_at && (
          <p className="text-[11px] text-[#6E6E6C]" data-testid="smtp-last-test">
            Laatste test: {new Date(smtp.last_test_at).toLocaleString("nl-BE", { dateStyle: "short", timeStyle: "short" })}
            {" — "}{smtp.last_test_message}
          </p>
        )}
        {smtp.sender_warning && (
          <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-900" data-testid="smtp-sender-warning">
            {smtp.sender_warning}
          </div>
        )}
        <p className="text-xs text-[#6E6E6C]">
          Optioneel. Koppel je mailbox zodat je offertes rechtstreeks vanuit EmmaMeasure kan verzenden, inclusief de
          PDF-bijlage. Je kan altijd blijven kopiëren of openen in je eigen mailprogramma.
        </p>

        {/* v14l — Primaire actie: Emma-wizard */}
        <Button
          onClick={() => setWizardOpen(true)}
          className="w-full bg-[#B07A4F] hover:bg-[#8f6440] text-white h-11 rounded-xl gap-2"
          data-testid="email-wizard-btn"
        >
          <Sparkles className="w-4 h-4" />
          {smtp.connected ? "Emma, help mij mijn mailbox opnieuw koppelen" : "Emma, help mij mijn e-mail koppelen"}
        </Button>

        <p className="text-[11px] text-[#8B8B87] italic flex items-center gap-1">
          <Info className="w-3 h-3" />
          Je wachtwoord/app-wachtwoord wordt versleuteld opgeslagen en is na opslaan niet opnieuw zichtbaar.
        </p>

        <EmailWizard
          open={wizardOpen}
          onOpenChange={setWizardOpen}
          onSaved={reload}
        />

        {/* v14l — Geavanceerde instellingen (collapsible) — bestaande handmatige SMTP-config */}
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          className="w-full flex items-center gap-2 text-sm font-semibold text-[#5D7259] hover:text-[#3f5b3b] pt-2"
          data-testid="smtp-advanced-toggle"
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${advancedOpen ? "" : "-rotate-90"}`} />
          Geavanceerde instellingen
        </button>
        {advancedOpen && (
        <div className="space-y-3 pt-1" data-testid="smtp-advanced-panel">
        <p className="text-[11px] text-[#8B8B87]">
          Voor eigen mailservers of providers die niet in de wizard staan. Vul host, poort, gebruikersnaam en
          wachtwoord manueel in.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-sm">SMTP host</Label>
            <Input value={smtp.host || ""}
              onChange={(e) => updateSmtp("host", e.target.value)}
              placeholder="smtp.gmail.com"
              data-testid="smtp-host" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Poort</Label>
            <Input type="number" value={smtp.port || 587}
              onChange={(e) => updateSmtp("port", e.target.value)}
              placeholder="587"
              data-testid="smtp-port" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Gebruikersnaam / e-mailadres</Label>
            <Input value={smtp.user || ""}
              onChange={(e) => updateSmtp("user", e.target.value)}
              placeholder="jij@domein.be"
              data-testid="smtp-user" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Wachtwoord / app-password</Label>
            <Input type="password" value={smtp.password || ""}
              onChange={(e) => updateSmtp("password", e.target.value)}
              placeholder={smtp.connected ? smtp.password_masked || "•••••• (opgeslagen)" : "app-password of wachtwoord"}
              data-testid="smtp-password" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Afzendernaam (optioneel)</Label>
            <Input value={smtp.from_name || ""}
              onChange={(e) => updateSmtp("from_name", e.target.value)}
              placeholder="YD Creaties"
              data-testid="smtp-from-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Afzenderadres (optioneel)</Label>
            <Input type="email" value={smtp.from_email || ""}
              onChange={(e) => updateSmtp("from_email", e.target.value)}
              placeholder="jij@domein.be"
              data-testid="smtp-from-email" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={!!smtp.use_tls}
            onChange={(e) => updateSmtp("use_tls", e.target.checked)}
            data-testid="smtp-tls-toggle" />
          <span>Gebruik TLS (aanbevolen)</span>
        </label>
        {smtpTestResult && (
          <div className={`text-xs rounded-lg border px-3 py-2 ${
            smtpTestResult.ok
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`} data-testid="smtp-test-result">
            {smtpTestResult.message}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button onClick={saveSmtp} disabled={smtpSaving}
            className="bg-[#B07A4F] hover:bg-[#8f6440] text-white h-10 rounded-xl"
            data-testid="smtp-save-btn">
            <Save className="w-4 h-4" />
            {smtpSaving ? "Bezig…" : "SMTP opslaan"}
          </Button>
          <Button onClick={testSmtp} disabled={smtpTesting || !smtp.connected}
            variant="outline" className="rounded-xl h-10 border-[#5D7259] text-[#5D7259]"
            data-testid="smtp-test-btn">
            {smtpTesting ? "Testen…" : "Test verbinding"}
          </Button>
          <Button onClick={sendTestMail} disabled={smtpTesting || !smtp.connected}
            variant="outline" className="rounded-xl h-10 border-[#5D7259] text-[#5D7259]"
            data-testid="smtp-test-email-btn">
            Stuur testmail naar mezelf
          </Button>
          {smtp.connected && (
            <Button onClick={disconnectSmtp} variant="outline"
              className="rounded-xl h-10 border-red-300 text-red-700 hover:bg-red-50"
              data-testid="smtp-disconnect-btn">
              Verbinding verbreken
            </Button>
          )}
        </div>
        </div>
        )}
      </div>
    </div>
  );
}
